import { yellowOrangeRedPalette } from "@appretio/shared";
import { primitiveTokens } from "@appretio/ui-kit";
import type {
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions,
  AgScatterSeriesItemStylerParams,
  AgScatterSeriesTooltipRendererParams
} from "ag-charts-enterprise";
import { useMemo } from "react";
import { ReviewDataResponse } from "../../../../pages/inSeasonReviewPage/hooks/useFetchReviewData";
import type { TrendLineResponse } from "../../../../pages/inSeasonReviewPage/hooks/useFetchTrendLine";
import { OLSTrendLineTooltip } from "../tooltip/OLSTrendLineTooltip";
import { StyleOptionAccuracyTooltip } from "../tooltip/StyleOptionAccuracyTooltip";

const X_KEY = "time_purchase";
const Y_KEY = "nis3";

const TITLE_ACCURACY = "Accuracy";
const TITLE_ST_50 = "50% ST";
const TITLE_ST_100 = "100% ST";

const X_NAME_AXES = "Time purchase";
const Y_NAME_AXES = "NIS III";
const TREND_LINE_TITLE = "Overall trend line (OLS)";

export type InSeasonReviewRow = ReviewDataResponse[number];

export interface UseAccuracyChartOptionsProps {
  /**
   * In-season review data to be displayed in the accuracy chart.
   */
  inSeasonReviewData: ReviewDataResponse;
  /**
   * Trend line data for the accuracy chart.
   * If an empty array is passed (fallback), the trend line will be hidden.
   */
  trendLineData: TrendLineResponse;
}

/**
 * @param inSeasonReviewData - In-season review data to be displayed in the chart.
 * @param trendLineData - Trend line data for the accuracy chart.
 * @returns Chart options for the accuracy chart.
 * Hook to generate chart options for visualizing accuracy in-season.
 * Renders a scatter plot with:
 * - X-axis: Time of purchase
 * - Y-axis: NIS III value
 * - Points colored by week_zero_50_st using a yellow-orange-red gradient
 * Lines included:
 * - 100% ST line (y = x) is a reference line indicating perfect accuracy.
 * - 50% ST line (y = 0.5 * x) shows the threshold for acceptable accuracy.
 * - Trend line (OLS) over all points
 */

export function useAccuracyChartOptions({
  inSeasonReviewData,
  trendLineData
}: UseAccuracyChartOptionsProps) {
  return useMemo(() => {
    const validData = inSeasonReviewData.filter(
      (d) => Number.isFinite(d.time_purchase) && Number.isFinite(d.nis3)
    );

    const hasData = validData.length > 0;
    const hasTrendLineData =
      trendLineData !== undefined && !Array.isArray(trendLineData);

    const [minTime = 0, maxTime = 0] = hasData
      ? [
          Math.min(...validData.map((d) => d.time_purchase as number)),
          Math.max(...validData.map((d) => d.time_purchase as number))
        ]
      : [0, 0];

    const series: AgCartesianSeriesOptions[] = [
      /**
       * Scatter plot of accuracy points colored by week_zero_50_st.
       */
      {
        type: "scatter",
        title: TITLE_ACCURACY,
        data: validData,
        xKey: X_KEY,
        yKey: Y_KEY,
        cursor: "pointer",
        itemStyler: (params: AgScatterSeriesItemStylerParams) => {
          const row = params.datum;
          const color = yellowOrangeRedPalette(row.week_zero_50_st ?? 0);
          return { fill: color, stroke: color };
        },
        tooltip: {
          range: "exact",
          renderer: (params: AgScatterSeriesTooltipRendererParams) =>
            StyleOptionAccuracyTooltip(params.datum)
        },
        visible: hasData
      },
      /**
       * 100% ST line (y = x) is a reference line indicating perfect accuracy.
       */
      {
        type: "line",
        title: TITLE_ST_100,
        cursor: "pointer",
        data: [
          { time_purchase: minTime, nis3: minTime },
          { time_purchase: maxTime, nis3: maxTime }
        ],
        xKey: X_KEY,
        yKey: Y_KEY,
        stroke: primitiveTokens.colors.graph.green[400],
        strokeWidth: 2,
        marker: {
          enabled: true,
          size: 5,
          fill: primitiveTokens.colors.graph.green[400]
        },
        visible: hasData,
        tooltip: {
          range: "exact",
          renderer: () => ({
            title: TITLE_ST_100
          })
        }
      },
      /**
       * 50% ST line (y = 0.5 * x) shows the threshold for acceptable accuracy.
       */
      {
        type: "line",
        cursor: "pointer",
        title: TITLE_ST_50,
        data: [
          { time_purchase: minTime, nis3: minTime * 0.5 },
          { time_purchase: maxTime, nis3: maxTime * 0.5 }
        ],
        xKey: X_KEY,
        yKey: Y_KEY,
        stroke: primitiveTokens.colors.graph.red[400],
        strokeWidth: 2,
        marker: {
          enabled: true,
          size: 5,
          fill: primitiveTokens.colors.graph.red[400]
        },
        visible: hasData,
        tooltip: {
          range: "exact",
          renderer: () => ({ title: TITLE_ST_50 })
        }
      },
      /**
       * Trend line (OLS) over all points
       */
      {
        type: "line",
        title: TREND_LINE_TITLE,
        data: hasTrendLineData
          ? validData.map((d) => ({
              time_purchase: d.time_purchase,
              nis3:
                trendLineData.slope * (d.time_purchase as number) +
                trendLineData.intercept
            }))
          : [],
        xKey: X_KEY,
        yKey: Y_KEY,
        stroke: primitiveTokens.colors.graph.red[700],
        strokeWidth: 2,
        marker: {
          enabled: true,
          fill: primitiveTokens.colors.graph.red[700],
          size: 5
        },
        tooltip: {
          range: "exact",
          renderer: (params) => {
            if (!hasTrendLineData) {
              return { title: TREND_LINE_TITLE };
            }
            const { intercept, r_squared, slope } = trendLineData;
            return OLSTrendLineTooltip({
              intercept: intercept,
              nis3: params.datum.nis3,
              r_squared: r_squared,
              slope: slope,
              time_purchase: params.datum.time_purchase
            });
          }
        },
        visible: hasData && hasTrendLineData
      }
    ];

    const axes: AgCartesianAxisOptions[] = [
      {
        type: "number",
        position: "bottom",
        title: { text: X_NAME_AXES },
        nice: true,
        keys: [X_KEY]
      },
      {
        type: "number",
        position: "left",
        title: { text: Y_NAME_AXES },
        nice: true,
        keys: [Y_KEY]
      }
    ];

    return { series, axes };
  }, [inSeasonReviewData, trendLineData]);
}
